import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./MarketStatus.css";

function MarketStatus() {
    const [activeTab, setActiveTab] = useState("marketStatus");
    const [stocks, setStocks] = useState([]);
    const [filter, setFilter] = useState("BEARISH");
    const [isSubmitActive, setIsSubmitActive] = useState(false);
    const [pendingUpdates, setPendingUpdates] = useState({});
    const [selectedStocks, setSelectedStocks] = useState({});
    const [visibleStocks, setVisibleStocks] = useState([]);
    const [bullishCount, setBullishCount] = useState(0);
    const [bearishCount, setBearishCount] = useState(0);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [showLiveBBConfirmation, setShowLiveBBConfirmation] = useState(false);

    // ✅ Load from localStorage first; fallback to API if empty
    useEffect(() => {
        const savedStocks = localStorage.getItem("marketStatusStocks");
        if (savedStocks) {
            const parsed = JSON.parse(savedStocks);
            setStocks(parsed);
            const bullish = parsed.filter((s) => s.companyStatus === "BULLISH").length;
            const bearish = parsed.filter((s) => s.companyStatus === "BEARISH").length;
            setBullishCount(bullish);
            setBearishCount(bearish);
        } else {
            axios
                .get("https://prod-erp.nifty10.in/get/company")
                .then((response) => {
                    const fetchedStocks = response.data.data || [];
                    setStocks(fetchedStocks);
                    const bullish = fetchedStocks.filter((s) => s.companyStatus === "BULLISH").length;
                    const bearish = fetchedStocks.filter((s) => s.companyStatus === "BEARISH").length;
                    setBullishCount(bullish);
                    setBearishCount(bearish);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    toast.error("Failed to fetch company data.");
                });
        }
    }, []);

    // Auto update counts when stocks change
    useEffect(() => {
        const bullish = stocks.filter((s) => s.companyStatus === "BULLISH").length;
        const bearish = stocks.filter((s) => s.companyStatus === "BEARISH").length;
        setBullishCount(bullish);
        setBearishCount(bearish);
    }, [stocks]);

    // Filter effect for Market Status toggle
    useEffect(() => {
        setVisibleStocks(stocks);
        const updatedPendingUpdates = Object.fromEntries(
            stocks.map((stock) => [stock.companyCode, filter])
        );
        setPendingUpdates(updatedPendingUpdates);
        setIsSubmitActive(true);
        setVisibleStocks(
            stocks.map((stock) => ({
                ...stock,
                companyStatus: filter,
            }))
        );
    }, [filter, stocks]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        handleToggleChange("BEARISH");
    };

    const handleToggleChange = (newFilter) => {
        setFilter(newFilter);
        const defaultStatus = newFilter.toUpperCase();
        const updatedPendingUpdates = Object.fromEntries(
            stocks.map((stock) => [stock.companyCode, defaultStatus])
        );
        setPendingUpdates(updatedPendingUpdates);
        setIsSubmitActive(true);
        setVisibleStocks(
            stocks.map((stock) => ({
                ...stock,
                companyStatus: defaultStatus,
            }))
        );
    };

    const handleStatusChange = (companyCode, newStatus) => {
        setPendingUpdates((prev) => ({
            ...prev,
            [companyCode]: newStatus,
        }));
        setIsSubmitActive(true);
    };

    // ✅ Market Status: Local update + Save to localStorage
    const proceedMarketStatusUpdate = () => {
        const updatedStocks = stocks.map((stock) => ({
            ...stock,
            companyStatus: pendingUpdates[stock.companyCode] || stock.companyStatus,
        }));

        setStocks(updatedStocks);
        localStorage.setItem("marketStatusStocks", JSON.stringify(updatedStocks)); // 💾 save

        setPendingUpdates({});
        setIsSubmitActive(false);
        toast.success("Market status updated locally!", {
            position: "top-right",
        });
        setShowConfirmation(false);
    };

    const getPendingStatusCounts = () => {
        let bullish = 0;
        let bearish = 0;
        stocks.forEach((stock) => {
            const newStatus = pendingUpdates[stock.companyCode] || stock.companyStatus;
            if (newStatus === "BULLISH") bullish++;
            else if (newStatus === "BEARISH") bearish++;
        });
        return { bullish, bearish };
    };

    const handleCheckboxChange = (event, companyCode) => {
        setSelectedStocks((prevSelected) => {
            const updatedSelected = { ...prevSelected };
            if (event.target.checked) {
                if (Object.keys(updatedSelected).length < 10) {
                    updatedSelected[companyCode] = true;
                } else {
                    toast.warn("Maximum of 10 stocks can be selected!", {
                        position: "top-right",
                    });
                }
            } else {
                delete updatedSelected[companyCode];
            }
            return updatedSelected;
        });
    };

    const totalSelectedCount = Object.keys(selectedStocks).length;

    const isStockDisabled = (stock) => {
        return totalSelectedCount === 10 && !selectedStocks[stock.companyCode];
    };

    const getSelectedBBStocksGrouped = () => {
        const bullish = [];
        const bearish = [];
        stocks.forEach((stock) => {
            if (selectedStocks[stock.companyCode]) {
                if (stock.companyStatus === "BULLISH") bullish.push(stock.companyName);
                else bearish.push(stock.companyName);
            }
        });
        return { bullish, bearish };
    };

    // ✅ Live BB: Final API + Save to localStorage
    const handleProceed = async () => {
        if (totalSelectedCount === 0) {
            toast.warn("Please select at least one stock to proceed!", {
                position: "top-right",
            });
            return;
        }

        const updatedStockData = stocks.map((stock) => ({
            ...stock,
            liveBB: !!selectedStocks[stock.companyCode],
        }));

        try {
            await axios.put(
                "https://prod-erp.nifty10.in/company/bulk/update/company",
                updatedStockData.map(({ companyId, companyStatus, liveBB }) => ({
                    companyId,
                    companyStatus,
                    liveBB,
                }))
            );

            toast.success("Live BB status updated successfully!", {
                position: "top-right",
            });

            setStocks(updatedStockData);
            localStorage.setItem("marketStatusStocks", JSON.stringify(updatedStockData)); // 💾 save

            setSelectedStocks({});
            handleTabChange("liveBB");
        } catch (error) {
            console.error("API Error:", error.response?.data || error.message);
            toast.error("Failed to update Live BB status!", {
                position: "top-right",
            });
        } finally {
            setShowLiveBBConfirmation(false);
        }
    };

    const liveBBStocks = stocks.slice().sort((a, b) => a.companyName.localeCompare(b.companyName));

    return (
        <div id="marketStatusContainer" className="market-status-container">
            <div className="markets-header-container">
                <div className="status-counts">
                    <span className="count-box bullish-count">Bullish: {bullishCount}</span>
                    <span className="count-box bearish-count">Bearish: {bearishCount}</span>
                </div>

                <div className="tab-container">
                    <button
                        className={`tab ${activeTab === "marketStatus" ? "active" : ""}`}
                        onClick={() => handleTabChange("marketStatus")}
                    >
                        Market Status
                    </button>
                    <button
                        className={`tab ${activeTab === "liveBB" ? "active" : ""}`}
                        onClick={() => handleTabChange("liveBB")}
                    >
                        Live BB
                    </button>
                </div>
                <ToastContainer position="top-right" style={{ marginTop: "65px" }} />

                {activeTab === "marketStatus" && (
                    <div className="toggle-switch-container">
                        <span className="toggle-text">BEARISH</span>
                        <div>
                            <label className="switch-button" htmlFor="switch">
                                <div className="switch-outer">
                                    <input
                                        id="switch"
                                        type="checkbox"
                                        checked={filter === "BULLISH"}
                                        onChange={() =>
                                            handleToggleChange(filter === "BULLISH" ? "BEARISH" : "BULLISH")
                                        }
                                    />
                                    <div className="button">
                                        <span className="button-toggle"></span>
                                        <span className="button-indicator"></span>
                                    </div>
                                </div>
                            </label>
                        </div>
                        <span className="toggle-text">BULLISH</span>
                    </div>
                )}
            </div>

            {/* Market Status Tab */}
            {activeTab === "marketStatus" && (
                <>
                    <div className="market-stocks-container">
                        {visibleStocks
                            .slice()
                            .sort((a, b) => a.companyName.localeCompare(b.companyName))
                            .map((stock) => (
                                <div key={stock.companyCode} className="stock-box">
                                    <span className="stock-symbol">{stock.companyName}</span>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                name={`status-${stock.companyCode}`}
                                                value="BULLISH"
                                                checked={
                                                    pendingUpdates[stock.companyCode] !== undefined
                                                        ? pendingUpdates[stock.companyCode] === "BULLISH"
                                                        : stock.companyStatus === "BULLISH"
                                                }
                                                onChange={() =>
                                                    handleStatusChange(stock.companyCode, "BULLISH")
                                                }
                                            />
                                            Bullish
                                        </label>
                                        <label>
                                            <input
                                                type="radio"
                                                name={`status-${stock.companyCode}`}
                                                value="BEARISH"
                                                checked={
                                                    pendingUpdates[stock.companyCode] !== undefined
                                                        ? pendingUpdates[stock.companyCode] === "BEARISH"
                                                        : stock.companyStatus === "BEARISH"
                                                }
                                                onChange={() =>
                                                    handleStatusChange(stock.companyCode, "BEARISH")
                                                }
                                            />
                                            Bearish
                                        </label>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <button
                        className="market-submit-button"
                        disabled={!isSubmitActive}
                        onClick={() => setShowConfirmation(true)}
                    >
                        Submit
                    </button>
                </>
            )}

            {/* Market Status Confirmation Modal */}
            {showConfirmation && (
                <div className="confirmation-modal-overlay">
                    <div className="confirmation-modal">
                        <div className="modal-icon">⚠️</div>
                        <p>Do you want to change the status?</p>
                        {(() => {
                            const { bullish, bearish } = getPendingStatusCounts();
                            return (
                                <div className="popup-status-counts">
                                    <span className="popup-count bullish-count">Bullish: {bullish}</span>
                                    <span className="popup-count bearish-count">Bearish: {bearish}</span>
                                </div>
                            );
                        })()}
                        <div className="modal-actions">
                            <button className="no-btn" onClick={() => setShowConfirmation(false)}>No</button>
                            <button className="yes-btn" onClick={proceedMarketStatusUpdate}>Yes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Live BB Confirmation Modal */}
            {showLiveBBConfirmation && (
                <div className="confirmation-modal-overlay">
                    <div className="confirmation-modal">
                        <div className="modal-icon">⚠️</div>
                        <p style={{ fontWeight: "bold", marginTop: "10px" }}>Selected BB Stocks</p>
                        {(() => {
                            const { bullish, bearish } = getSelectedBBStocksGrouped();
                            return (
                                <div className="bb-stocks-list">
                                    <div>
                                        <h4 style={{ color: "#146c2d" }}>Bullish</h4>
                                        <div className="stock-grid">
                                            {bullish.map((name, i) => (
                                                <span key={i} className="stock-pill">{name}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ marginTop: "12px" }}>
                                        <h4 style={{ color: "#b30000" }}>Bearish</h4>
                                        <div className="stock-grid">
                                            {bearish.map((name, i) => (
                                                <span key={i} className="stock-pill">{name}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="modal-actions">
                            <button className="no-btn" onClick={() => setShowLiveBBConfirmation(false)}>No</button>
                            <button className="yes-btn" onClick={handleProceed}>Yes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Live BB Tab */}
            {activeTab === "liveBB" && (
                <>
                    {/* Bearish Stocks */}
                    <div className="live-bb-container">
                        {liveBBStocks
                            .filter((stock) => stock.companyStatus === "BEARISH")
                            .map((stock) => (
                                <div
                                    key={stock.companyCode}
                                    className={`stock-box live-stock-box ${isStockDisabled(stock) && !selectedStocks[stock.companyCode]
                                        ? "disabled-container"
                                        : ""
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="live-checkbox"
                                        checked={!!selectedStocks[stock.companyCode]}
                                        onChange={(e) => handleCheckboxChange(e, stock.companyCode)}
                                        disabled={isStockDisabled(stock)}
                                    />
                                    <span className="stock-symbol">{stock.companyName}</span>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                name={`status-${stock.companyCode}`}
                                                value={stock.companyStatus}
                                                checked={
                                                    stock.companyStatus === "BULLISH" ||
                                                    stock.companyStatus === "BEARISH"
                                                }
                                                disabled={isStockDisabled(stock)}
                                            />
                                            {stock.companyStatus === "BULLISH" ? "Bullish" : "Bearish"}
                                        </label>
                                    </div>
                                </div>
                            ))}
                    </div>

                    {/* Bullish Stocks */}
                    <div className="live-bb-container">
                        {liveBBStocks
                            .filter((stock) => stock.companyStatus === "BULLISH")
                            .map((stock) => (
                                <div
                                    key={stock.companyCode}
                                    className={`stock-box live-stock-box ${isStockDisabled(stock) && !selectedStocks[stock.companyCode]
                                        ? "disabled-container"
                                        : ""
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="live-checkbox"
                                        checked={!!selectedStocks[stock.companyCode]}
                                        onChange={(e) => handleCheckboxChange(e, stock.companyCode)}
                                        disabled={isStockDisabled(stock)}
                                    />
                                    <span className="stock-symbol">{stock.companyName}</span>
                                    <div className="radio-group">
                                        <label>
                                            <input
                                                type="radio"
                                                name={`status-${stock.companyCode}`}
                                                value={stock.companyStatus}
                                                checked={
                                                    stock.companyStatus === "BULLISH" ||
                                                    stock.companyStatus === "BEARISH"
                                                }
                                                disabled={isStockDisabled(stock)}
                                            />
                                            {stock.companyStatus === "BULLISH" ? "Bullish" : "Bearish"}
                                        </label>
                                    </div>
                                </div>
                            ))}
                    </div>

                    <button
                        className="market-submit-button"
                        onClick={() => setShowLiveBBConfirmation(true)}
                        disabled={totalSelectedCount === 0}
                    >
                        Proceed
                    </button>
                </>
            )}
        </div>
    );
}

export default MarketStatus;
